// @ts-nocheck

import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { LoadingSpinner } from 'Containers/ui'
import {
  getCampaignToSchedule,
  getAmountOfCampaigns,
} from 'Redux/selectors/campaigns'
import { PurchaseTypeEnum, paymentCancelledEvent } from 'Helpers/tagManager'
import { getUserSelector } from 'Redux/selectors/auth'
import { CampaignToSchedule, CampaignPlatformTypeEnum } from 'Types/index'
import { PaymentMethodEnum, Roles } from 'Constants/enums'
import spotifyIcon from 'Shared/img/svg/spotify.svg'
import { Desktop } from 'Shared/components/Responsive'
import { IconProtection, IconLockMobile } from 'Stories/Icon/Icon.stories'

import PaypalButton from 'Containers/ScheduleCampaign/components/PaypalButtonOld/PaypalButton'
import { useBudget } from '../../hooks'
import { CheckoutProps } from './props'
import {
  Container,
  Divider,
  Title,
  Row,
  TrackNameRow,
  RowText,
  SpotifyIcon,
  ButtonsContainer,
  ContinueButton,
  PromoCodeButton,
  PaypalButtonWrapper,
  ContinueButtonPaypal,
  CampaignsTracksPromocode,
  CampaignsTracksPromocodeDiscount,
  CampaignsTracksPromocodeExist,
  AlertProtectionWrapper,
  AlertProtectionWrapperText,
  SecurePaymentBlock,
  SecurePaymentText,
} from './styles'
import { useCheckout } from '../../../../Hooks/payment'
import { useBluesnapPayments } from '../../../../Hooks/bluesnap'

export const Checkout: React.FC<CheckoutProps> = ({
  isLoading,
  isCoupon,
  isContinueDisabled,
  budget,
  setIsScheduleFinished,
  onClickPromocode,
  onClickContinue,
  selectedDate,
  currentStep,
  VAT,
  total,
  balance,
  discount,
  paymentOption,
  onPaypalClickSendEvents,
}) => {
  const user = useSelector(getUserSelector)
  const amountOfCampaigns = useSelector(getAmountOfCampaigns)
  const campaignDetails: CampaignToSchedule = useSelector(getCampaignToSchedule)
  const { currencySymbol } = useBudget({})
  const { onSuccessPaypal } = useCheckout({
    setIsScheduleFinished,
    // @ts-ignore
    selectedDate,
  })
  const { calculateTotalPrice, isBluesnapInitialized } = useBluesnapPayments({})
  // @ts-ignore
  const { onClickPaypalEventSend } = useCheckout({})
  const { t } = useTranslation()

  const budgetRounded = (Math.round(budget / 10) * 10).toFixed(2)

  const continueText =
    currentStep === 3
      ? t('scheduleCampaignPage.confirm&Pay')
      : t('scheduleCampaignPage.continue')

  const discountAmount = useMemo(
    () => (isCoupon ? (budget + VAT) * (discount / 100) : 0),
    [isCoupon, budget, VAT]
  )

  const onCancelPaypal = () => {
    paymentCancelledEvent({
      campaignType: campaignDetails.type,
      campaignPlatformType: CampaignPlatformTypeEnum.SPOTIFY,
      campaignNumber: amountOfCampaigns,
      campaignId: campaignDetails.id,
      userId: user.id,
      userRole: user.role,
      campaignValue: campaignDetails?.price,
      genresList: campaignDetails?.genres.map(genre => genre.name).join(),
      languagesList: campaignDetails?.languages
        .map(language => language.name)
        .join(),
      isFeedCampaign: false,
      isDanceRequired: false,
      paymentAmount: campaignDetails?.price,
      purchaseType:
        campaignDetails.type === 'first'
          ? PurchaseTypeEnum.FirstPurchase
          : PurchaseTypeEnum.Purchase,
      paymentMethod: PaymentMethodEnum.PayPal,
    })
  }

  const renderContinue = (): ReactElement => (
    <>
      {paymentOption === 'paypal' && currentStep === 3 && (
        <PaypalButtonWrapper>
          <ContinueButtonPaypal disabled={isContinueDisabled}>
            {continueText}
          </ContinueButtonPaypal>
          <PaypalButton
            campaignId={campaignDetails.id}
            price={campaignDetails.price}
            total={calculateTotalPrice()}
            sendPaymentInfoEvent={onClickPaypalEventSend}
            onSuccess={onSuccessPaypal}
            onClick={onPaypalClickSendEvents}
            onCancelPaypal={onCancelPaypal}
          />
        </PaypalButtonWrapper>
      )}

      {paymentOption === 'creditCard' && currentStep === 3 && (
        <ContinueButton
          disabled={isContinueDisabled || isLoading || isBluesnapInitialized}
          onClick={onClickContinue}
        >
          {isLoading ? <LoadingSpinner /> : continueText}
        </ContinueButton>
      )}

      {paymentOption === 'stripe' && currentStep === 3 && (
        <ContinueButton onClick={onClickContinue}>
          {isLoading ? <LoadingSpinner /> : continueText}
        </ContinueButton>
      )}

      {currentStep === 1 && (
        <ContinueButton
          disabled={isContinueDisabled || isLoading || isBluesnapInitialized}
          onClick={onClickContinue}
        >
          {isLoading ? <LoadingSpinner /> : continueText}
        </ContinueButton>
      )}
      {currentStep === 2 && (
        <ContinueButton
          disabled={
            isContinueDisabled || isLoading || isBluesnapInitialized || !budget
          }
          onClick={onClickContinue}
        >
          {isLoading ? <LoadingSpinner /> : continueText}
        </ContinueButton>
      )}
    </>
  )

  return (
    <Container>
      <Title>{t('scheduleCampaignPage.campaignSummary')}</Title>
      <Row>
        <TrackNameRow>
          <SpotifyIcon src={spotifyIcon} alt="" />
          <RowText>{`${campaignDetails?.track?.author} - ${campaignDetails?.track?.title}`}</RowText>
        </TrackNameRow>
        <RowText>{`${currencySymbol} ${budgetRounded}`}</RowText>
      </Row>
      <Divider />
      <Row>
        <RowText>{t('scheduleCampaignPage.subtotal')}</RowText>
        <RowText>{`${currencySymbol} ${budgetRounded}`}</RowText>
      </Row>
      <Row>
        <RowText>{t('scheduleCampaignPage.balanceCheckout')}</RowText>
        <RowText>{`${currencySymbol} ${balance}`}</RowText>
      </Row>
      {!!VAT && (
        <Row>
          <RowText>{t('scheduleCampaignPage.vat')}</RowText>
          <RowText>{`${currencySymbol} ${VAT}`}</RowText>
        </Row>
      )}
      {isCoupon && (
        <CampaignsTracksPromocode>
          <CampaignsTracksPromocodeDiscount>
            {discount}% {t('scheduleCampaignPage.discount')}
          </CampaignsTracksPromocodeDiscount>
          <CampaignsTracksPromocodeExist>
            <span>{currencySymbol}</span>
            &nbsp;
            {discountAmount.toFixed(2)}
          </CampaignsTracksPromocodeExist>
        </CampaignsTracksPromocode>
      )}
      <Divider />
      <Row>
        <RowText bold>{t('scheduleCampaignPage.total')}</RowText>
        <RowText bold>{`${currencySymbol} ${total}`}</RowText>
      </Row>
      {!isCoupon && (
        <PromoCodeButton onClick={onClickPromocode}>
          +{t('scheduleCampaignPage.promocode')}
        </PromoCodeButton>
      )}
      <ButtonsContainer>{renderContinue()}</ButtonsContainer>
      <Desktop>
        {currentStep === 3 && (
          <>
            <SecurePaymentBlock>
              <IconLockMobile />
              <SecurePaymentText>
                {t('scheduleCampaignPage.ssl')}
              </SecurePaymentText>
            </SecurePaymentBlock>
            <AlertProtectionWrapper>
              <IconProtection />
              <AlertProtectionWrapperText>
                <span>{t('scheduleCampaignPage.protectionTitle')}</span>
                <span>{t('scheduleCampaignPage.artistProtection')}</span>
              </AlertProtectionWrapperText>
            </AlertProtectionWrapper>
          </>
        )}
      </Desktop>
    </Container>
  )
}
